// useKanbanDnd — DndContext root + onDragEnd handler for the Wishes.tsx
// kanban. The draggable / droppable hooks live inside KanbanCard /
// KanbanColumn (where they belong per @dnd-kit's colocated-state
// pattern); this hook just wires the context and resolves the drop.
//
// Per `docs/spec/wishlist/DESIGN.md §5.7`:
//   - 3px activation distance (PointerSensor) so a tap doesn't start a
//     drag (mirrors v2's stable kanban-dnd logic from W14.5 retro).
//   - dropped into the abandoned column → `ConfirmDialog` (destructive)
//     with the calendar-mirror warning (DATA_MODEL §5.1). W34.3.2
//     replaces the v1 `window.confirm` with a proper ConfirmDialog that
//     auto-promotes to BottomSheet on mobile.

import React, { useCallback, useMemo, useState } from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { ConfirmDialog } from "@/components/patterns/ConfirmDialog";
import type { WishStatus, WishSummary } from "@/lib/api/wishlist";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queries/keys";

const ACTIVATION_DISTANCE = 3; // tap vs drag separator per DESIGN §5.7
const ABANDONED_STATUS: WishStatus = "abandoned";
const ABANDONED_CONFIRM_TEXT =
  "確定放棄此心願？日曆鏡像會一併移除。";

export interface UseKanbanDndArgs {
  wishes: WishSummary[];
  /** Called when a wish is dropped into a new column. */
  onMove: (wishId: string, newStatus: WishStatus) => Promise<void> | void;
  /** Set false to disable drag entirely (e.g. read-only review). */
  enabled: boolean;
}

export interface KanbanDndHandles {
  /** Currently dragged wish id (or null). */
  activeId: string | null;
  /** Render this once at the top of the kanban tree. */
  DndContextRoot: React.FC<{ children: React.ReactNode }>;
  /**
   * Controlled ConfirmDialog for abandoned-column drops. Render alongside
   * `DndContextRoot` so the dialog overlay can sit above the kanban.
   * W34.3.2: replaces the v1 `window.confirm(...)` interaction.
   */
  AbandonedConfirm: React.FC;
}

export function useKanbanDnd({
  wishes,
  onMove,
  enabled,
}: UseKanbanDndArgs): KanbanDndHandles {
  const [activeId, setActiveId] = useState<string | null>(null);
  // W34.3.2: dialog pending target. `null` = closed.
  const [abandonTarget, setAbandonTarget] = useState<WishSummary | null>(null);
  const queryClient = useQueryClient();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: ACTIVATION_DISTANCE },
    }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over) return;
      const wishId = String(active.id);
      // KanbanColumn registers itself with `id="column-${status}"`.
      const overId = String(over.id);
      if (!overId.startsWith("column-")) return;
      const newStatus = overId.slice("column-".length) as WishStatus;
      const wish = wishes.find((w) => w.id === wishId);
      if (!wish) return;
      if (wish.status === newStatus) return;
      if (newStatus === ABANDONED_STATUS) {
        // W34.3.2: open the ConfirmDialog (mobile → BottomSheet) instead
        // of `window.confirm`. The actual move is gated on confirm.
        setAbandonTarget(wish);
        return;
      }
      await onMove(wishId, newStatus);
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
    [wishes, onMove, queryClient],
  );

  const DndContextRoot = useMemo(() => {
    // useMemo factory returns a component-like wrapper using createElement
    // (no JSX in a .tsx hook file per `react-refresh/only-export-components`).
    const Component: React.FC<{ children: React.ReactNode }> = ({
      children,
    }) =>
      React.createElement(
        DndContext,
        {
          sensors: enabled ? sensors : [],
          onDragStart: enabled ? handleDragStart : undefined,
          onDragEnd: enabled ? handleDragEnd : undefined,
        },
        children,
      );
    Component.displayName = "KanbanDndContextRoot";
    return Component;
  }, [enabled, sensors, handleDragStart, handleDragEnd]);

  const AbandonedConfirm = useMemo(() => {
    // useMemo factory returns a component (avoids JSX in .tsx hook file).
    const Component: React.FC = () =>
      React.createElement(ConfirmDialog, {
        open: abandonTarget !== null,
        onOpenChange: (o: boolean) => !o && setAbandonTarget(null),
        destructive: true,
        title: "放棄此心願",
        description: ABANDONED_CONFIRM_TEXT,
        confirmLabel: "放棄",
        onConfirm: async () => {
          const target = abandonTarget;
          setAbandonTarget(null);
          if (target) {
            await onMove(target.id, ABANDONED_STATUS);
            void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
          }
        },
      });
    Component.displayName = "KanbanAbandonedConfirm";
    return Component;
  }, [abandonTarget, onMove, queryClient]);

  return {
    activeId,
    DndContextRoot,
    AbandonedConfirm,
  };
}